// The portfolio status banner (BA P1.6a): the version clients see now, the
// version under review, and the company's reason when it asked for changes.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Clock, Eye, EyeOff } from 'lucide-react';
import type { MyBaProfile, ReviewerBaProfile } from '@/services/baProfileService';
import { portfolioStatus } from '../portfolio';

const fmt = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : '');

export const StatusBanner: React.FC<{ profile: MyBaProfile | ReviewerBaProfile }> = ({ profile }) => {
  const { t } = useTranslation('brandAmbassadors');
  const s = portfolioStatus(profile);
  return (
    <div className="grid gap-2 md:grid-cols-2" data-testid="status-banner">
      <div className={`flex items-start gap-2 rounded-md p-3 text-sm ${s.live ? 'bg-success-soft text-success-strong' : 'bg-muted text-muted-foreground'}`}
        data-testid="live-status">
        {s.live ? <Eye className="w-4 h-4 mt-0.5" /> : <EyeOff className="w-4 h-4 mt-0.5" />}
        <span>
          {s.live
            ? t('status.live', { defaultValue: 'Live: version {{n}}, published {{date}}', n: s.live.revisionNo, date: fmt(s.live.publishedAt) })
            : s.removed
              ? t('status.removed', { defaultValue: 'Not in the catalogue (removed).' })
              : t('status.not_live', { defaultValue: 'Not in the catalogue yet.' })}
        </span>
      </div>
      {s.underReview ? (
        <div className="flex items-start gap-2 rounded-md p-3 text-sm bg-info-soft text-info-strong" data-testid="review-status">
          <Clock className="w-4 h-4 mt-0.5" />
          <span>{t('status.under_review', { defaultValue: 'Version {{n}} is under review (sent {{date}}). Clients keep seeing the live version until it’s published.',
            n: s.underReview.revisionNo, date: fmt(s.underReview.submittedAt) })}</span>
        </div>
      ) : s.changesRequested ? (
        <div className="flex items-start gap-2 rounded-md p-3 text-sm bg-warning-soft text-warning-strong" data-testid="review-status">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>
            {t('status.changes_requested', { defaultValue: 'Changes requested on version {{n}}:', n: s.changesRequested.revisionNo })}{' '}
            <q data-testid="review-reason">{s.changesRequested.reason}</q>
          </span>
        </div>
      ) : s.approvedNotLive ? (
        <div className="flex items-start gap-2 rounded-md p-3 text-sm bg-info-soft text-info-strong" data-testid="review-status">
          <Clock className="w-4 h-4 mt-0.5" />
          <span>{t('status.approved_not_live', { defaultValue: 'Version {{n}} is approved and waiting to be published.', n: s.approvedNotLive.revisionNo })}</span>
        </div>
      ) : null}
    </div>
  );
};
